/**
 * React hook that bridges the imperative MapLibre Map API to the
 * declarative React component model.
 *
 * The MapLibre :class:`Map` is constructed against a DOM
 * container element; React's render lifecycle is the wrong place
 * to mount it. The hook owns the imperative lifecycle:
 *
 * - On mount: construct the :class:`Map` against the container
 *   ref, wire the ``load`` callback to ``onMapReady`` and the
 *   ``error`` callback to the operator-visibility log path
 *   (CLAUDE.md §8.1 — fail-safe defaults; show what the system
 *   is doing).
 * - On unmount: call ``map.remove()`` to release WebGL resources
 *   + DOM listeners.
 *
 * # Theme flips are NOT a ``setStyle`` swap
 *
 * This hook deliberately does **not** hot-swap the style on a theme
 * change. A full ``map.setStyle()`` tears down the entire style
 * document — including the snapshot / cluster / trail / track-tail /
 * event sources+layers that :file:`Map.tsx` adds imperatively on the
 * map's ``'load'`` event. Those data layers are keyed on the loaded
 * map instance, not on the style, so they would be wiped by the swap
 * and never re-added — every marker would vanish on a toggle. The
 * theme flip is instead a *targeted* source/paint update performed
 * by :func:`applyMapTheme` (``use-map-theme.ts``), driven from a
 * :file:`Map.tsx` effect keyed on the resolved theme. This is the
 * locked decision for the dark-theme PR.
 *
 * The hook returns helpers (``getBounds``, ``flyTo``) that callers
 * use to drive viewport changes from the React tree without
 * threading the map instance through props. The map instance is
 * intentionally **not** in React state — calling ``setState`` on
 * every camera move would re-render the canvas at every frame.
 *
 * Cross-references:
 *   - REQ-UI-002 (MapLibre GL JS).
 *   - REQ-UI-007 (viewport-change → BFF subscription update; M3+).
 *   - ADR-016 §4 (WebGL rendering).
 *   - CLAUDE.md §8.1 (operator-visibility — log errors loudly).
 */
import { useEffect, useRef } from "react";
import {
  AttributionControl,
  Map,
  NavigationControl,
  ScaleControl,
  type IControl,
  type StyleSpecification,
  type LngLatLike,
} from "maplibre-gl";
import type { DistanceUnit, MapHome } from "@/auth/session-types";
import { configureMaplibreWorker } from "@/map/maplibre-worker";
import { registerPmtilesProtocol } from "@/map/pmtiles-protocol";
import { installBasemapReadiness, type MapDegradedInfo } from "@/map/basemap-readiness";

/**
 * MapLibre :class:`ScaleControl` unit string. MapLibre's scale bar renders in
 * one of these built-in units; this is a narrowing of the control's own
 * ``unit`` option (no public type is exported by maplibre-gl for it).
 */
export type ScaleUnit = "nautical" | "metric" | "imperial";

/**
 * Map a Profile distance-unit preference to the MapLibre scale-bar unit
 * (REQ-UI-083, ADR-033 §7).
 *
 * The Profile's ``units.distance`` (:type:`DistanceUnit`) drives every
 * distance render; for the on-canvas scale bar that means selecting the
 * matching built-in MapLibre :type:`ScaleUnit`:
 *
 *   - ``nautical_miles`` → ``"nautical"``
 *   - ``kilometers``     → ``"metric"``
 *   - ``statute_miles``  → ``"imperial"``
 *
 * NOTE: this drives MapLibre's *built-in* scale rendering (the control
 * computes the bar width + label itself), so the increment-5
 * :func:`formatDistance` primitive is intentionally NOT this control's
 * backing — ``formatDistance``'s first render site is a future
 * detail-panel range/coverage field (still pending), not the scale bar.
 *
 * @param unit - The Profile distance unit, or ``undefined`` when the
 *     working state carries no ``units`` slice.
 * @returns The MapLibre scale unit; defaults to ``"nautical"`` when
 *     ``unit`` is absent (matches the historical interim default — see
 *     the :class:`ScaleControl` construction in :func:`useMap`).
 *
 * Cross-references:
 *   - REQ-UI-083 (distance-unit preference); ADR-033 §7.
 */
export function distanceUnitToScaleUnit(unit: DistanceUnit | undefined): ScaleUnit {
  switch (unit) {
    case "kilometers":
      return "metric";
    case "statute_miles":
      return "imperial";
    case "nautical_miles":
    case undefined:
      return "nautical";
    default:
      // Exhaustiveness guard — a new DistanceUnit member added to the
      // generated schema must be handled explicitly rather than silently
      // falling through (CLAUDE.md §8.1 conservative defaults). The
      // ``never`` assignment makes such an addition a compile error.
      return assertNeverUnit(unit);
  }
}

/**
 * Compile-time exhaustiveness guard for :func:`distanceUnitToScaleUnit`.
 * Reached only if a new :type:`DistanceUnit` member is added without a
 * matching ``case`` above. Defaults to ``"nautical"`` at runtime
 * (fail-safe) while the ``never`` parameter forces the compile error.
 */
function assertNeverUnit(unit: never): ScaleUnit {
  void unit;
  return "nautical";
}

/**
 * Initial-viewport options for :func:`useMap`.
 *
 * The default is the **global** view (see :const:`DEFAULT_INITIAL_VIEWPORT`),
 * matching the built-in default Profile's ``map.home`` so the first paint
 * never flashes a regional framing before the Profile home applies.
 * Cross-network determinism (CLAUDE.md §5.7): the default is a bundle
 * constant; operators pan/zoom from there, and a Profile Apply re-targets
 * nav-home (REQ-UI-090).
 *
 * Cross-references:
 *   - REQ-UI-024 (saved viewport per ADR-022).
 *   - REQ-UI-090 (nav-home → Profile home); ADR-033 §9.4.
 *   - docs/engineering/detail/storyboards/shared/fixtures.md
 *     "Operating area".
 */
export interface MapInitialViewport {
  /** Map center as ``[lng, lat]`` per MapLibre's
   *  :type:`LngLatLike`. */
  center: LngLatLike;
  /** Initial zoom level. */
  zoom: number;
}

/**
 * Default initial viewport — the **global** view.
 *
 * Center ``[0, 20]`` / zoom ``1.5`` mirrors the built-in default Profile's
 * ``map.home`` (``packages/meridian_bff/.../builtin_profiles/default-global.json``
 * → ``map.home = { center: { lon: 0, lat: 20 }, zoom: 1.5 }``). Matching the
 * built-in home is the load-bearing detail of the post-inspection viewport
 * fix (item 3c): the map is constructed with this viewport on first mount, so
 * before the Profile home applies (or when no Profile is active) the operator
 * sees the global framing rather than a CONUS flash. The "Black Diamond Range"
 * fixture uses notional coordinates only, so no regional default is warranted.
 *
 * Cross-references:
 *   - REQ-UI-090 (nav-home → Profile home); ADR-033 §9.4 (the built-in home
 *     this mirrors).
 *   - docs/engineering/detail/storyboards/shared/fixtures.md.
 */
export const DEFAULT_INITIAL_VIEWPORT: MapInitialViewport = {
  center: [0, 20] as LngLatLike,
  zoom: 1.5,
};

/**
 * Convert a Profile :type:`MapHome` (``{ center: { lon, lat }, zoom }``;
 * ADR-033 §9.4) to a :type:`MapInitialViewport` MapLibre can consume
 * (``center`` as the ``[lon, lat]`` tuple).
 *
 * Returns ``null`` when ``home`` is absent or its coordinates aren't finite
 * numbers — the caller then falls back to :const:`DEFAULT_INITIAL_VIEWPORT`,
 * so a partial/garbage home never flies the camera to ``NaN`` (CLAUDE.md
 * §8.1 fail-safe; a bad home is a no-op, not a broken viewport).
 *
 * @param home - The Profile's ``map.home``, or ``undefined``.
 * @returns The viewport, or ``null`` when no usable home is present.
 *
 * Cross-references:
 *   - REQ-UI-090 (nav-home → Profile home); ADR-033 §9.4.
 */
export function profileHomeToViewport(home: MapHome | undefined): MapInitialViewport | null {
  if (home === undefined) {
    return null;
  }
  const { lon, lat } = home.center;
  if (!Number.isFinite(lon) || !Number.isFinite(lat) || !Number.isFinite(home.zoom)) {
    return null;
  }
  return { center: [lon, lat] as LngLatLike, zoom: home.zoom };
}

/**
 * Options for :func:`useMap`.
 */
export interface UseMapOptions {
  /** Style returned by :func:`buildMapStyle`. */
  style: StyleSpecification;
  /** Initial viewport. Defaults to :const:`DEFAULT_INITIAL_VIEWPORT`. */
  initial?: MapInitialViewport;
  /**
   * Getter for the **live** nav-home target — the Profile's
   * ``map.home`` viewport (ADR-033 §9.4; REQ-UI-090). The reset-view
   * control reads this **on every click** (not at construction) so a
   * later Profile Apply re-targets nav-home without rebuilding the
   * control. Returns ``null`` when the working state carries no home,
   * in which case the control flies to :const:`DEFAULT_INITIAL_VIEWPORT`.
   *
   * Omitted in tests / call sites that only need the static initial
   * viewport — the control then falls back to the captured ``initial``.
   */
  getHome?: () => MapInitialViewport | null;
  /**
   * Initial MapLibre scale-bar unit (REQ-UI-083). Defaults to
   * ``"nautical"`` (the historical interim default). Live changes flow
   * through :meth:`MapHandle.setScaleUnit`, driven from a
   * :file:`Map.tsx` effect keyed on ``units.distance``.
   */
  scaleUnit?: ScaleUnit;
  /**
   * Called once the map's ``load`` event fires (style + first
   * tile request complete). Markers and event glyphs (M2 / M3)
   * mount via this callback.
   */
  onMapReady?: (map: Map) => void;
  /**
   * Called when MapLibre emits an ``error`` event. Defaults to a
   * ``console.error`` so operator-visibility holds (CLAUDE.md
   * §8.1) — silent failures are forbidden. Override to surface a
   * toast or update connection-status; M2 wires the connection
   * indicator.
   */
  onMapError?: (error: ErrorEvent) => void;
  /**
   * Called, at most once, when the map is ready **without** a working
   * basemap (REQ-UI-166). Two ways in, and they are not the same:
   *
   *   - the basemap errored and rendered no tiles, whether or not
   *     MapLibre's ``load`` fired — the maplibre-gl 6.x case, where the map
   *     loads and the markers are fine but the background is gone; or
   *   - ``load`` never arrived at all, and ``onMapReady`` is being driven by
   *     the watchdog instead (it fires immediately afterwards, so the data
   *     layers still mount over the theme background).
   *
   * Defaults to a ``console.error``. :file:`Map.tsx` overrides it with the
   * operator-visible notification, because a blank background with markers
   * floating on it is otherwise indistinguishable from "no terrain here"
   * (CLAUDE.md §8.1 — operator visibility).
   *
   * Read through the live options ref, not captured at mount, so the
   * handler a later render supplies is the one that runs.
   */
  onMapDegraded?: (info: MapDegradedInfo) => void;
}

/**
 * Imperative helpers exposed to React callers.
 *
 * Callers use these instead of holding a reference to the
 * :class:`Map` directly so the hook can manage the imperative
 * lifecycle (and so the map's underlying canvas is never
 * re-mounted on render).
 */
export interface MapHandle {
  /** Get the current viewport bounds. Returns ``null`` before the
   *  map has loaded. */
  getBounds: () => { west: number; south: number; east: number; north: number } | null;
  /** Animate the camera to a target.
   *
   *  Wraps :meth:`Map.flyTo`. No-op before the map has loaded
   *  (load races against the toast-driven "Locate on map" link in
   *  Flow 02 Frame 4). */
  flyTo: (target: { center: LngLatLike; zoom?: number }) => void;
  /** Set the scale-bar unit live (REQ-UI-083).
   *
   *  Wraps the :class:`ScaleControl`'s ``setUnit``. Driven from a
   *  :file:`Map.tsx` effect keyed on ``units.distance`` so changing the
   *  Profile distance unit re-renders the scale bar without rebuilding
   *  the map. No-op before the control has been added. */
  setScaleUnit: (unit: ScaleUnit) => void;
}

/**
 * Mount a MapLibre :class:`Map` against ``containerRef.current``.
 *
 * @param containerRef - React ref to the DOM element that will
 *     host the map's canvas. The element must have non-zero
 *     dimensions before this hook runs.
 * @param options - Style + initial viewport + lifecycle callbacks.
 * @returns A :type:`MapHandle` for imperative camera control.
 *
 * Cross-references:
 *   - REQ-UI-002 (MapLibre GL JS).
 *   - ADR-016 §4.
 *   - CLAUDE.md §8.1 (operator-visibility on errors).
 */
export function useMap(
  containerRef: React.RefObject<HTMLDivElement | null>,
  options: UseMapOptions,
): MapHandle {
  // Map instance lives in a ref so re-renders never re-mount the
  // canvas. The mount-effect below has no dependencies on the
  // options object's identity (style hot-swap uses a separate
  // effect) so the map survives re-renders that change the
  // ``onMapReady`` closure.
  const mapRef = useRef<Map | null>(null);

  // The MapLibre :class:`ScaleControl` instance, held so
  // :meth:`MapHandle.setScaleUnit` can call ``setUnit`` live when the
  // operator's ``units.distance`` preference changes (REQ-UI-083). The
  // control is constructed once in the mount effect; this ref is the
  // only handle to it after that.
  const scaleControlRef = useRef<ScaleControl | null>(null);

  // The hook's mount effect intentionally captures the *initial*
  // options once. Subsequent option changes are handled by
  // separate effects (style swap below) or by reading through a ref
  // (the live nav-home getter). This pattern mirrors MapLibre's own
  // React-bridge guidance.
  const initialRef = useRef(options);

  // Always-current view of ``options`` so closures constructed once at
  // mount (the reset-view control) read the *latest* ``getHome`` getter
  // rather than the one captured at first render. ``options`` is a fresh
  // object every render, so this keeps the control reading the live
  // working-state home after a Profile Apply (REQ-UI-090) without
  // re-mounting the map.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      // Defensive — the component owning the ref guarantees the
      // element exists. Log loudly per CLAUDE.md §8.1 rather than
      // silently no-op.
      console.error("[meridian/map/use-map] container ref is null at mount");
      return undefined;
    }

    const { style, initial, onMapReady, onMapError } = initialRef.current;
    const viewport = initial ?? DEFAULT_INITIAL_VIEWPORT;

    // Must precede Map construction: the constructor acquires MapLibre's
    // worker pool immediately, and MapLibre 6's default derivation points
    // at an asset Vite never emits (404 → the map silently never starts).
    // Idempotent — subsequent mounts are a no-op.
    configureMaplibreWorker();

    // Must precede Map construction: MapLibre resolves the style's source
    // URLs at construction time, so a pmtiles:// basemap descriptor
    // (REQ-UI-148) needs the protocol registered first. Idempotent —
    // subsequent mounts are a no-op.
    registerPmtilesProtocol();

    const map = new Map({
      container,
      style,
      center: viewport.center,
      zoom: viewport.zoom,
      // Default attribution control disabled here; re-added bottom-LEFT
      // below so the lower-right corner is free for the cursor coordinate
      // readout (:func:`CursorReadout`, #292) and the scale bar. The
      // re-added control still surfaces the BFF-tile-proxy attribution
      // string from the style's sources; the storyboard chrome owns
      // operator-facing classification banners per ADR-024 §10.
      attributionControl: false,
      // No symbol cross-fade on tile swaps. The default (300 ms) starts an
      // animation every time a GeoJSON source's data changes, so under live
      // track load — a swap per commit — the whole scene repainted
      // continuously (feature spec ``ui-track-load-headroom`` §2.1 / §2.2).
      // Meridian's data layers swap on every commit by design; the fade is
      // a basemap nicety the operator never sees at these rates.
      fadeDuration: 0,
      // Constrain horizontal scroll to a single world copy.
      // Without this the map repeats east-west; the viewport-sync
      // helper would push out-of-range longitudes (< -180 or > 180)
      // which the BFF rejects with HTTP 422 (Pydantic
      // ge=-180/le=180 on /api/query/snapshot). Restricting to one
      // world copy keeps the operator's spatial reference frame
      // intact and (combined with the defensive clamp in
      // :func:`installViewportSync`) keeps BFF queries within
      // accepted ranges. Per ADR-016 §3 (operator-tooling spatial
      // reasoning) + the 2026-05-27 empty-map diagnostic follow-up
      // (issue #3 of the rendering cascade).
      //
      // **maxBounds intentionally omitted.** An earlier revision
      // (PR #245) added ``maxBounds: [[-180, -85.0511], [180,
      // 85.0511]]`` to also constrain the vertical pan to the
      // Mercator-visible range, but the 360°-wide bounds (sw_lng
      // = -180, ne_lng = +180 are the same meridian in MapLibre's
      // canonicalisation) collapsed to a degenerate single-meridian
      // constraint that snapped the default center to lng=180 and
      // returned a near-zero-width viewport — producing the
      // pathological URL surfaced in the post-#245 diagnostic
      // (``min_longitude=179.99…``, ``max_longitude=180.00…``).
      // MapLibre clamps panning past the Mercator north/south
      // singularities (~±85.0511°) internally; ``renderWorldCopies:
      // false`` handles east-west. So ``maxBounds`` is redundant
      // for our constraint goals and dangerous at the edges.
      renderWorldCopies: false,
      // ``preserveDrawingBuffer`` left at default (false). The
      // visual-verification skill can still screenshot via the
      // browser-level capture path that `take_screenshot` uses;
      // enabling this would cost render perf for no operator
      // benefit (REQ-XPERF-UI-INITIAL-RENDER).
    });
    mapRef.current = map;

    // Navigation controls — +/- zoom buttons + compass-reset
    // button. MapLibre's stock control; placed top-right per the
    // storyboard's chrome convention (operator-facing on-canvas
    // affordances live in the corners opposite the left rail).
    // Double-click-to-zoom-in + scroll-wheel zoom remain enabled
    // by default; this adds the discoverable buttons operators
    // expect (Splunk / Datadog / ArcGIS muscle memory).
    map.addControl(new NavigationControl({ visualizePitch: false }), "top-right");

    // "Reset view" control — flies the camera to the operator's
    // nav-home (REQ-UI-090). The target is read **live on every click**
    // via a getter that returns the Profile's ``map.home`` (ADR-033
    // §9.4) from the working state, so a later Profile Apply re-targets
    // nav-home with no map rebuild. The getter is read through
    // ``optionsRef`` (always the latest render's ``getHome``) and falls
    // back to the captured initial ``viewport`` when no home is set —
    // the same fallback the camera was constructed with. Future
    // preferences work re-targets purely by feeding a different
    // ``getHome``; this control never needs to change again.
    map.addControl(
      makeResetViewControl(map, () => optionsRef.current.getHome?.() ?? viewport),
      "top-right",
    );

    // Attribution, bottom-LEFT. Relocated from MapLibre's default
    // bottom-right so the lower-right corner is free for the cursor
    // coordinate readout (:func:`CursorReadout`, #292) and the scale bar.
    // ``compact`` makes it a small "ⓘ" that expands on click; the text is
    // sourced from the style's tile-source ``attribution`` field.
    map.addControl(new AttributionControl({ compact: true }), "bottom-left");
    // Force the compact attribution **collapsed until the operator clicks**
    // (item 3b). A bare once-removal of the expanded classes loses a race:
    // MapLibre's ``_updateCompact`` re-runs on every ``styledata`` /
    // ``sourcedata`` / ``resize`` (via ``_updateAttributions``), and the
    // first run AFTER the tile sources load — when the ``<details>`` element
    // still lacks ``maplibregl-compact`` because ``onAdd`` saw an empty
    // attribution — re-adds ``open`` + ``maplibregl-compact-show``, so the
    // banner pops back open on its own and eats canvas space. The enforcer
    // below collapses any programmatic re-expansion until a genuine user
    // click on the ⓘ summary sets the intent flag. Disposer disconnects the
    // observer on unmount.
    const attribEl = container.querySelector<HTMLElement>(".maplibregl-ctrl-attrib");
    const disposeAttrib = attribEl !== null ? enforceClickOnlyAttribution(attribEl) : undefined;

    // Scale bar — lower-right (Flow 02 chrome), stacked just above the
    // cursor coordinate readout (#292) via map-controls.css so the two
    // lower-right affordances don't overlap. The unit is driven by the
    // operator's ``units.distance`` preference (REQ-UI-083; ADR-033 §7)
    // mapped via :func:`distanceUnitToScaleUnit`; ``"nautical"`` is the
    // default when no units slice is set. The control is held in
    // ``scaleControlRef`` so :meth:`MapHandle.setScaleUnit` can call
    // ``setUnit`` live when the preference changes. Styled for light +
    // dark in map-controls.css (REQ-UI-040). This uses MapLibre's
    // built-in scale rendering, so the increment-5 ``formatDistance``
    // primitive is NOT its backing (see :func:`distanceUnitToScaleUnit`).
    const scaleControl = new ScaleControl({
      unit: initialRef.current.scaleUnit ?? "nautical",
      maxWidth: 120,
    });
    scaleControlRef.current = scaleControl;
    map.addControl(scaleControl, "bottom-right");

    // Readiness, guarded. ``onMapReady`` still runs from MapLibre's ``load``
    // exactly as before, so the healthy path is unchanged. The guard adds two
    // things (REQ-UI-166; basemap-readiness.ts has the mechanism):
    //
    //   1. if ``load`` never arrives — a CSP-blocked or black-holed tile host
    //      holds it forever on some MapLibre versions — a watchdog promotes
    //      the map anyway, so the data layers mount over the theme background
    //      instead of the operator getting a blank display with no basemap
    //      AND no markers while platform data arrives normally;
    //   2. a basemap that errored and rendered nothing is reported through
    //      ``onMapDegraded`` even when ``load`` did fire, which is the case
    //      that otherwise leaves the operator with no diagnostic at all.
    const disposeReadiness = installBasemapReadiness(map, {
      onReady: () => {
        if (onMapReady) {
          onMapReady(map);
        }
      },
      // Read live (not the mount-captured closure) for the same reason the
      // reset-view control reads ``getHome`` live — the handler is supplied
      // by a hook whose identity the mount effect never sees again.
      onDegraded: (info) => {
        const handler = optionsRef.current.onMapDegraded ?? defaultMapDegradedHandler;
        handler(info);
      },
    });

    map.on("error", (event) => {
      const handler = onMapError ?? defaultMapErrorHandler;
      // MapLibre's error event carries a synthetic ErrorEvent on
      // ``event.error``; pass the underlying through so handlers
      // can inspect ``.error.name``.
      handler(event as unknown as ErrorEvent);
    });

    return () => {
      // Cancel the readiness watchdog + drop its listeners before the map
      // goes away, so a pending deadline can never fire against a removed
      // map (REQ-UI-166).
      disposeReadiness();
      // Disconnect the attribution collapse-enforcer's observer first so
      // it never fires against a torn-down element (item 3b).
      disposeAttrib?.();
      // ``remove()`` releases the WebGL context, removes DOM
      // listeners and unbinds the canvas. Per MapLibre docs +
      // ADR-016 §4 (WebGL rendering — context lifetimes matter).
      map.remove();
      mapRef.current = null;
      scaleControlRef.current = null;
    };
    // containerRef is stable (a ref) — eslint-react-hooks does
    // not need it in deps, but adding it documents intent.
    // The map is constructed once per component lifetime; option
    // changes are applied by the separate effect below.
  }, [containerRef]);

  // NOTE: there is intentionally no style hot-swap effect here.
  // Theme flips are applied as a targeted source/paint update by
  // :func:`applyMapTheme` (``use-map-theme.ts``), driven from a
  // :file:`Map.tsx` effect keyed on the resolved theme — NOT via
  // ``map.setStyle()``, which would tear down the imperatively-added
  // data layers (snapshot / cluster / trail / track-tail / event)
  // and make every marker vanish on toggle. See the module docstring
  // ("Theme flips are NOT a setStyle swap") for the full rationale.
  // ``options.style`` is consumed once at construction (the mount
  // effect reads ``initialRef.current.style``); subsequent renders
  // do not rebuild the canvas.

  return {
    getBounds: () => {
      const map = mapRef.current;
      if (!map) {
        return null;
      }
      const bounds = map.getBounds();
      return {
        west: bounds.getWest(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        north: bounds.getNorth(),
      };
    },
    flyTo: (target) => {
      const map = mapRef.current;
      if (!map) {
        return;
      }
      map.flyTo({
        center: target.center,
        zoom: target.zoom,
      });
    },
    setScaleUnit: (unit) => {
      // No-op before the control is constructed (map not yet mounted).
      scaleControlRef.current?.setUnit(unit);
    },
  };
}

/**
 * Default error-handler — logs to ``console.error`` per CLAUDE.md
 * §8.1 (operator-visibility; silent failures are forbidden).
 *
 * Surface-quality work (toast on tile-fetch errors, connection-
 * indicator flip on persistent failures) lives in M2's
 * connection-status wiring. M1 only guarantees the failure is
 * visible to the on-call engineer via browser devtools.
 */
function defaultMapErrorHandler(event: ErrorEvent): void {
  // Unwrap MapLibre's nested error so the actual decode / fetch
  // message reaches the console. The outer event object's
  // ``toString`` renders as ``[object N]`` in aggregated DevTools
  // output, which hides the underlying ``InvalidStateError`` /
  // tile / sourceId fields engineers need for diagnosis.
  const e = event as ErrorEvent & {
    error?: Error;
    sourceId?: string;
    tile?: { tileID?: { canonical?: { z: number; x: number; y: number } } };
  };
  const inner = e.error;
  const tile = e.tile?.tileID?.canonical;
  const tileStr = tile ? `${tile.z}/${tile.x}/${tile.y}` : undefined;
  console.error(
    "[meridian/map] MapLibre error:",
    inner?.name ?? "Error",
    inner?.message ?? String(event),
    { sourceId: e.sourceId, tile: tileStr },
  );
}

/**
 * Default degraded-map handler — logs to ``console.error`` per CLAUDE.md
 * §8.1 (operator-visibility; silent failures are forbidden).
 *
 * This is the *engineer* half of the diagnostic and the fallback for call
 * sites that mount a map without the chrome (tests, storybook-style
 * harnesses). :file:`Map.tsx` overrides it with the operator-visible
 * notification — the console is not a surface a deployed operator reads, and
 * engineering has no access to their browser (CLAUDE.md §5.4).
 *
 * Cross-references:
 *   - REQ-UI-166; map/basemap-readiness.ts.
 */
function defaultMapDegradedHandler(info: MapDegradedInfo): void {
  console.error(
    "[meridian/map] basemap did not load within",
    `${info.timeoutMs}ms —`,
    "mounting the data layers over the theme background so the operator still",
    "sees platform markers (REQ-UI-166).",
    {
      reason: info.reason,
      sourceId: info.sourceId,
      detail: info.message,
      // The confirmed trigger, named so the on-call engineer starts in the
      // right place: the deployment's CSP must allow-list the tile host the
      // BFF advertises via GET /api/config (REQ-UI-097).
      likelyCause:
        info.reason === "basemap-source-error"
          ? "tile host unreachable or blocked by the deployment CSP (REQ-UI-097)"
          : "tile host did not respond",
    },
  );
}

/**
 * Force MapLibre's compact attribution control **collapsed until the operator
 * clicks the ⓘ** — never on load, hover, style/source data, or resize (item
 * 3b).
 *
 * # Why a once-removal isn't enough
 *
 * MapLibre 4.x renders the attribution as a ``<details>`` element and toggles
 * its expanded state in ``_updateCompact``, which runs on ``onAdd`` **and**
 * again from ``_updateAttributions`` on every ``styledata`` / ``sourcedata`` /
 * ``resize``. Because ``onAdd`` first sees an *empty* attribution (the tile
 * sources haven't reported yet), the element starts without the
 * ``maplibregl-compact`` marker; the first ``_updateCompact`` after the
 * sources load therefore takes the "add compact" branch and re-applies
 * ``open`` + ``maplibregl-compact-show``. A single class removal at add-time
 * is overwritten by that later pass, so the banner pops open by itself and
 * eats canvas space. (There is no public API on :class:`AttributionControl`
 * to suppress this.)
 *
 * # Strategy
 *
 * Collapse immediately, then keep it collapsed via a :class:`MutationObserver`
 * on the ``<details>`` element until the operator expresses intent by clicking
 * the ``<summary>`` toggle:
 *
 *   - A ``click`` on the ``<summary>`` sets ``userToggled`` — from then on the
 *     observer stands down and the native ``<details>`` open/close is the
 *     operator's to control (click to expand, click to collapse).
 *   - Until then, any mutation that adds ``open`` or ``maplibregl-compact-show``
 *     (i.e. MapLibre's auto-expand) is immediately reverted. The revert itself
 *     mutates the element, but it only ever *removes* those markers, so it
 *     converges (no observer feedback loop) and is idempotent.
 *
 * Returns a disposer that disconnects the observer + detaches the click
 * listener; the caller invokes it in the map's unmount cleanup so the observer
 * never fires against a removed element. Exported (module-internal, no barrel)
 * so the unit test can drive the auto-expand → re-collapse path directly.
 *
 * Cross-references:
 *   - REQ-UI-033 / REQ-UI-034 (BFF tile-proxy attribution string).
 *   - ADR-016 §4 (operator chrome — data is the figure, chrome the ground).
 */
export function enforceClickOnlyAttribution(detailsEl: HTMLElement): () => void {
  let userToggled = false;

  /** Strip the expanded markers unless the operator opened it themselves. */
  const collapse = (): void => {
    if (userToggled) {
      return;
    }
    if (detailsEl.hasAttribute("open") || detailsEl.classList.contains("maplibregl-compact-show")) {
      detailsEl.classList.remove("maplibregl-compact-show");
      detailsEl.removeAttribute("open");
    }
  };

  // Initial collapse — covers the synchronous ``onAdd`` expansion.
  collapse();

  // A real click on the ⓘ summary hands control back to the operator. Use
  // capture so we record intent before the native ``<details>`` toggle (and
  // MapLibre's own click handler) runs.
  const summaryEl = detailsEl.querySelector<HTMLElement>("summary");
  const onSummaryClick = (): void => {
    userToggled = true;
  };
  summaryEl?.addEventListener("click", onSummaryClick, { capture: true });

  // Revert MapLibre's programmatic auto-expand (open attr + compact-show
  // class) until the operator has clicked. ``MutationObserver`` may be
  // absent in some non-DOM test contexts — degrade to the initial collapse
  // only (fail-safe; the banner is non-critical chrome per CLAUDE.md §8.1).
  let observer: MutationObserver | null = null;
  if (typeof MutationObserver !== "undefined") {
    observer = new MutationObserver(() => {
      collapse();
    });
    observer.observe(detailsEl, {
      attributes: true,
      attributeFilter: ["open", "class"],
    });
  }

  return () => {
    observer?.disconnect();
    summaryEl?.removeEventListener("click", onSummaryClick, { capture: true });
  };
}

/**
 * Build a "reset view" :class:`IControl` for MapLibre.
 *
 * Returns a control with a single button that flies the camera to the
 * operator's nav-home (REQ-UI-090). Used in tandem with MapLibre's stock
 * :class:`NavigationControl` so operators have a discoverable un-zoom path
 * after deep zoom / pan.
 *
 * The home target is supplied as a **getter** (``getTarget``), read on
 * **every click** rather than captured at construction. This is the
 * load-bearing detail for REQ-UI-090: a later Profile Apply changes where
 * "home" is, and the operator's nav-home must follow it without rebuilding
 * the control (which would require re-mounting the map). The caller threads
 * a getter that resolves the live working-state ``map.home`` (falling back
 * to the static initial viewport when none is set).
 *
 * The control's DOM mirrors MapLibre's built-in control idiom
 * (the same ``maplibregl-ctrl`` + ``maplibregl-ctrl-group``
 * classes used by ``NavigationControl``) so the visual treatment
 * matches and the focus / hover styles from
 * ``maplibre-gl.css`` apply without theming work.
 *
 * Exported (module-internal export, no barrel) so the unit test can drive
 * ``onAdd`` + the button click directly and assert it reads the *current*
 * getter value — without standing up a full MapLibre canvas.
 *
 * Cross-references:
 *   - REQ-UI-002 (MapLibre GL JS).
 *   - REQ-UI-059 (a11y — button has aria-label + title).
 *   - REQ-UI-090 (nav-home → Profile home); ADR-033 §9.4.
 *   - ADR-016 §4 (operator chrome).
 */
export function makeResetViewControl(
  map: Pick<Map, "flyTo">,
  getTarget: () => { center: LngLatLike; zoom: number },
): IControl {
  let containerEl: HTMLDivElement | null = null;
  return {
    onAdd(): HTMLElement {
      const container = document.createElement("div");
      container.className = "maplibregl-ctrl maplibregl-ctrl-group";
      const button = document.createElement("button");
      button.type = "button";
      button.className = "maplibregl-ctrl-reset-view";
      button.setAttribute("aria-label", "Reset map view");
      button.title = "Reset map view";
      // Inline SVG so the icon ships without an external asset
      // (CLAUDE.md §8.2 air-gap posture — no CDN refs). The
      // shape matches a "home" / "compass-center" affordance
      // common across mapping UIs (ArcGIS, Mapbox custom controls).
      button.innerHTML =
        '<span aria-hidden="true" class="maplibregl-ctrl-icon" ' +
        'style="display:flex;align-items:center;justify-content:center;width:100%;height:100%;">' +
        '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" ' +
        'fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
        '<path d="M3 12l9-9 9 9"/><path d="M5 10v10h14V10"/><path d="M10 20v-6h4v6"/>' +
        "</svg></span>";
      button.addEventListener("click", () => {
        // Read the live target on click (REQ-UI-090) so nav-home tracks
        // the active Profile's home after an Apply.
        const target = getTarget();
        map.flyTo({ center: target.center, zoom: target.zoom });
      });
      container.appendChild(button);
      containerEl = container;
      return container;
    },
    onRemove(): void {
      containerEl?.parentNode?.removeChild(containerEl);
      containerEl = null;
    },
  };
}
